import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { Event, NativePlatform } from '@fullstory/proto/fs/api/segment';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useContext, useEffect } from 'react';
import { Button, Flex } from '@fullstory/components';
import { useOptimizeUtil } from '../../hooks/useOptimizeUtil';
import { AppearancesContext, PreviewContext } from '@fullstory/signal-studio-context';
import { FsEvent } from '@fullstory/fs-script-api';
import { useBackendSelectorTrim } from '../../utils/optimize';
import {
  toSelectorPieces,
  serializeSelector as serializeSelectorTree,
} from '../../utils/selector-tree';
import { makePlatformSelectorDeps } from '../../utils';
import { SelectorRule } from '@fullstory/selectors';
import { useFlags } from '@fullstory/react-hooks';
import { fixSelector } from '../../utils/optimize/fixSelector';
import { fullstorySelectors } from '@fullstory/redux';
import { useSelector } from 'react-redux';

interface SelectorTrimButtonProps {
  selector: string;
  platforms: NativePlatform[];
  onStart?: () => void;
  onFinish: (selector: string, error?: string) => void;
  onPartial?: (selector: string) => void;
  trimTrigger?: boolean;
  setTrimTrigger?: (off: boolean) => void;
  abortController: AbortController;
  setAbortController: (controller: AbortController) => void;
  trimming?: boolean;
}

const { getOrgId } = fullstorySelectors;

export const SelectorTrimButton = ({
  selector,
  platforms,
  onStart,
  onFinish,
  onPartial = () => {},
  trimTrigger,
  setTrimTrigger,
  abortController,
  setAbortController,
  trimming,
}: SelectorTrimButtonProps) => {
  const orgId = useSelector(getOrgId);
  const flags = useFlags();
  const backendSelectorTrim = useBackendSelectorTrim();
  const appearances = useContext(AppearancesContext);
  const getMetricFn = useCallback(
    (selector: string): Metric | null => {
      if (!SelectorRule.isValid(selector) || !appearances) {
        return null;
      }
      const event: Event = {
        primary: null,
        deps: makePlatformSelectorDeps({ selector, platforms }),
      };
      return appearances.getMetricFn({ event });
    },
    [appearances, platforms],
  );
  const { rendererClient } = useContext(PreviewContext);

  const { fetchTotalVolume } = useOptimizeUtil(getMetricFn, rendererClient);

  const trim = useCallback(async () => {
    if (!selector) {
      return;
    }

    const newAbortController = new AbortController();
    setAbortController(newAbortController);

    onStart?.();

    const start = performance.now();

    const abortableFetchTotalVolume = (s: string) => {
      if (newAbortController.signal.aborted) {
        throw new Error('Aborted');
      }
      return fetchTotalVolume(s);
    };

    const abortableValidate = async (s1: string) => {
      if (newAbortController.signal.aborted) {
        throw new Error('Aborted');
      }

      const volume = await fetchTotalVolume(s1);
      return volume > 0;
    };

    const initialVolume = await abortableFetchTotalVolume(selector);

    // if the selector already has traffic, this will just stay the original
    let fixedSelector = selector;

    let volume = initialVolume;
    if (volume === 0 && trimTrigger) {
      if (!flags.featureFlags().AppSelectorDebug) {
        onFinish(selector);
        return;
      }
      const fixedSelectorPieces = await fixSelector(selector, abortableValidate, onPartial, true);
      fixedSelector = serializeSelectorTree(fixedSelectorPieces);
      // update the volume of the fixed selector
      volume = await abortableFetchTotalVolume(fixedSelector);
    }

    const trims: { [key: string]: number } = {};

    backendSelectorTrim
      .trim(orgId, fixedSelector, platforms, newAbortController)
      .then(({ selector: trimmedSelector, error }) => {
        onFinish(trimmedSelector, error);
        const endTrim = performance.now();

        FsEvent.recordCustomEventV2('trim-backend', {
          totalTime: endTrim - start,
          totalTrims: Object.values(trims).reduce((acc, v) => acc + v, 0),
          ...trims,
          startSelector: selector,
          endSelector: trimmedSelector,
          startSelectorLength: toSelectorPieces(selector)?.flat().length || -1,
          endSelectorLegnth: toSelectorPieces(trimmedSelector)?.flat().length || -1,
        });
      });
  }, [
    fetchTotalVolume,
    onFinish,
    onPartial,
    onStart,
    selector,
    setAbortController,
    trimTrigger,
    flags,
    orgId,
    platforms,
    backendSelectorTrim,
  ]);

  const abort = useCallback(() => {
    abortController.abort();
  }, [abortController]);

  useEffect(() => {
    if (trimTrigger) {
      if (trimming) {
        abort();
      } else if (setTrimTrigger) {
        trim();
        setTrimTrigger(false);
      }
    }
  }, [trimTrigger, setTrimTrigger, trimming, abort, trim]);

  const newFSTAColors = useFlags().featureFlags().AppNewFstaColors;

  if (!appearances) return null;

  return (
    <Flex data-component="SelectorTrimButton" justifyContent="space-between" gap={8}>
      <Button
        iconLeft={trimming ? 'ai-animated' : 'ai'}
        variant={newFSTAColors ? 'outline-intelligence-gradient' : 'outline-intelligence'}
        size="small"
        onClick={trimming ? abort : trim}
      >
        {trimming ? (
          <FormattedMessage defaultMessage="Cancel" id="47FYwb" />
        ) : (
          <FormattedMessage
            defaultMessage="{newFSTAColors, select, true {AI } other {}}Trim"
            id="WJy6AF"
            values={{ newFSTAColors }}
          />
        )}
      </Button>
    </Flex>
  );
};
